import React from "react";
import { Stack, FormControlLabel, Checkbox } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { updateStudentTermReport } from "../redux/actions/studentTermReportActions";
import { cleanFormData } from "../helpers/formHelpers";
import { studentTermReportActions } from "../redux/slices/studentTermReport";

import { StudentTermReportResponseDto } from "../shared/dtos/studentTermReport/StudentTermReportResponseDto";
import { StudentTermReportUpdateRequestDto } from "../shared/dtos/studentTermReport/StudentTermReportUpdateRequestDto";

const updateReportSchema = Yup.object().shape({
  classTeacherRemarks: Yup.string()
    .max(500, "Remarks cannot exceed 500 characters")
    .nullable(),
  principalRemarks: Yup.string()
    .max(500, "Remarks cannot exceed 500 characters")
    .nullable(),
  isPublished: Yup.boolean().required(),
});

interface UpdateStudentTermReportFormProps {
  report: StudentTermReportResponseDto;
  loading?: boolean;
  error: string | null;
}

const UpdateStudentTermReportForm: React.FC<
  UpdateStudentTermReportFormProps
> = ({ report, loading = false, error = null }) => {
  const dispatch = useAppDispatch();

  const initialValues: StudentTermReportUpdateRequestDto = {
    classTeacherRemarks: report?.classTeacherRemarks || "",
    principalRemarks: report?.principalRemarks || "",
    isPublished: report?.isPublished || false,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(studentTermReportActions.setErrorUpdating(null))
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={updateReportSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: StudentTermReportUpdateRequestDto = {
            classTeacherRemarks: rawCleaned.classTeacherRemarks || null,
            principalRemarks: rawCleaned.principalRemarks || null,
            isPublished: Boolean(values.isPublished),
          };

          dispatch(
            updateStudentTermReport({ id: report.id, values: cleanedValues }),
          );
        }}
      >
        {({
          values,
          handleChange,
          dirty,
        }: FormikProps<StudentTermReportUpdateRequestDto>) => (
          <Form>
            <Stack spacing={3} sx={{ width: "100%", mt: 1 }}>
              <FormTextField
                name="classTeacherRemarks"
                label="Class Teacher's Remarks"
                multiline
                rows={3}
              />

              <FormTextField
                name="principalRemarks"
                label="Principal's Remarks"
                multiline
                rows={3}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="isPublished"
                    checked={values.isPublished}
                    onChange={handleChange}
                    color="secondary"
                  />
                }
                label="Publish to Parent Portal (Visible to Parents)"
                sx={{ alignSelf: "flex-start" }}
              />

              <SubmitButton
                title="Update Report"
                loadingText="Updating..."
                loading={loading}
                disabled={!dirty || loading}
                startIcon={<SaveOutlinedIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default UpdateStudentTermReportForm;
